import React, { useState, useEffect } from "react";
import SearchFieldRow from "./SearchFieldRow";
import DateSearchRow from "./DateSearchRow";

/**
 * 計装部門用 詳細検索フォームコンポーネント
 * - 入力項目の管理
 * - 入力制限
 * - 親への検索条件送信（onFormChange）
 */
const KeisouForm = ({ onFormChange, criteria, document_category = [], document_type = [] }) => {

    /**
     * 初期状態（リセット用）
     * すべてのフィールド・条件・あいまい検索設定を含む
     */
    const defaultFormData = {
        // --- テキスト項目 ---
        auto_number: "",
        product_no: "",
        destination: "",
        customer: "",
        assignee: "",
        product_model: "",
        variety_name: "",
        remarks: "",
        title: "",

        // --- 検索条件（0 = を含む） ---
        auto_number_condition: "0",
        product_no_condition: "0",
        destination_condition: "0",
        customer_condition: "0",
        assignee_condition: "0",
        product_model_condition: "0",
        variety_name_condition: "0",
        remarks_condition: "0",
        title_condition: "0",

        // --- あいまい検索チェック ---
        auto_number_fuzzy: true,
        product_no_fuzzy: true,
        destination_fuzzy: true,
        customer_fuzzy: true,
        assignee_fuzzy: true,
        product_model_fuzzy: true,
        variety_name_fuzzy: true,
        remarks_fuzzy: true,
        title_fuzzy: true,

        // --- セレクト項目 ---
        document_category: "",
        document_type: "",
    };

    // 入力値のステート管理
    const [formData, setFormData] = useState(defaultFormData);

    /**
     * 初回マウント時：formData の全項目を親へ送信
     */
    useEffect(() => {
        if (onFormChange) {
            Object.entries(formData).forEach(([key, value]) => {
                onFormChange(key, value);
            });
        }
    // eslint-disable-next-line 
    }, []);

    /**
     * 親 criteria 更新時の動作
     * - 初期状態（キー2つ以下）→ フォームをリセット
     * - それ以外 → criteria をフォームにマージ（API 経由など）
     */
    useEffect(() => {
        // リセット条件
        if (criteria && Object.keys(criteria).length <= 2) {
            setFormData(defaultFormData);

            Object.entries(defaultFormData).forEach(([k, v]) =>
                onFormChange(k, v)
            );
            return;
        }

        // criteria マージ
        if (criteria && Object.keys(criteria).length > 2) {
            setFormData(prev => ({ ...prev, ...criteria }));
        }
    // eslint-disable-next-line 
    }, [criteria]);

    // 全角＝1文字、半角＝0.5文字でカウント
    const trimJapaneseByLength = (str, limit) => {
        let result = "";
        let length = 0;

        for (const ch of str) {
            const add = ch.match(/[!-~]/) ? 0.5 : 1;
            if (length + add > limit) break;
            result += ch;
            length += add;
        }
        return result;
    };

    // 全角英数字 → 半角英数字
    const toHankaku = (str) =>
        str.replace(/[！-～]/g, (ch) =>
            String.fromCharCode(ch.charCodeAt(0) - 0xFEE0)
    );

    /**
     * 入力イベント（全 field 共通）
     * - 入力制限
     * - 半角変換
     * - formData 更新
     * - 親コンポーネントへ onFormChange 通知
     */
    const handleChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;

        if (name === "document_category" || name === "document_type") {
            newValue = value === "" ? "" : Number(value);
        }
        
        switch (name) {
            case "auto_number":
                // 半角英数字＋ハイフンのみ、最大8文字
                newValue = toHankaku(value)
                    .replace(/[^0-9]/g, "")
                    .slice(0, 10);
                break;

            case "product_no":
                newValue = trimJapaneseByLength(value, 5);
                break;

            case "destination":
            case "customer":
            case "remarks":
                newValue = trimJapaneseByLength(value, 20);
                break;

            case "assignee":
            case "product_model":
            case "variety_name":
                newValue = trimJapaneseByLength(value, 10);
                break;

            case "title":
                newValue = trimJapaneseByLength(value, 64);
                break;

            default:
                break;
        }

        const updated = { ...formData, [name]: newValue };
        setFormData(updated);
        onFormChange && onFormChange(name, newValue);
    };

    return (
        <div className="detail-grid-2col">

            {/* ───────── LEFT COLUMN ───────── */}
            <div className="form-column">

                {/* オートナンバー */}
                <div className="form-row">
                    <SearchFieldRow
                        label="オートナンバー"
                        name="auto_number"
                        value={formData.auto_number}
                        onChange={handleChange}
                        condition={formData.auto_number_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, auto_number_condition: val }));
                            onFormChange("auto_number_condition", val);
                        }}
                        isFuzzy={formData.auto_number_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, auto_number_fuzzy: checked }));
                            onFormChange("auto_number_fuzzy", checked);
                        }}
                    />
                </div>

                {/* 製番 */}
                <div className="form-row">
                    <SearchFieldRow
                        label="製番"
                        name="product_no"
                        value={formData.product_no}
                        onChange={handleChange}
                        condition={formData.product_no_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, product_no_condition: val }));
                            onFormChange("product_no_condition", val);
                        }}
                        isFuzzy={formData.product_no_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, product_no_fuzzy: checked }));
                            onFormChange("product_no_fuzzy", checked);
                        }}
                    />
                </div>

                {/* 図面種類 */}
                <div className="form-row">
                    <div className="search-field">
                        <label> 図面種類（計装・システム製番）</label>
                        <select
                            name="document_category"
                            value={formData.document_category || ""}
                            onChange={handleChange}
                            style={{ flex: '1', width: '100%', border: '1px solid #fff' }}
                        >
                            <option value=""></option>
                            {document_category?.map((item) => (
                                <option key={item.display_idx} value={item.document_category}>
                                    {item.document_category_text}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* 仕向先 */}
                <div className="form-row">
                    <SearchFieldRow
                        label="仕向先"
                        name="destination"
                        value={formData.destination}
                        onChange={handleChange}
                        condition={formData.destination_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, destination_condition: val }));
                            onFormChange("destination_condition", val);
                        }}
                        isFuzzy={formData.destination_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, destination_fuzzy: checked }));
                            onFormChange("destination_fuzzy", checked);
                        }}
                    />
                </div>

                {/* 得意先 */}
                <div className="form-row">
                    <SearchFieldRow
                        label="得意先"
                        name="customer"
                        value={formData.customer}
                        onChange={handleChange}
                        condition={formData.customer_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, customer_condition: val }));
                            onFormChange("customer_condition", val);
                        }}
                        isFuzzy={formData.customer_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, customer_fuzzy: checked }));
                            onFormChange("customer_fuzzy", checked);
                        }}
                    />
                </div>

                {/* 日付条件 */}
                <div className="form-row">
                    <DateSearchRow
                        onChange={(value) => {
                            onFormChange("dateCondition", value);
                        }}
                    />
                </div>
            </div>


            {/* ───────── RIGHT COLUMN ───────── */}
            <div className="form-column">

                <div className="form-row">
                    <SearchFieldRow
                        label="担当者"
                        name="assignee"
                        value={formData.assignee}
                        onChange={handleChange}
                        condition={formData.assignee_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, assignee_condition: val }));
                            onFormChange("assignee_condition", val);
                        }}
                        isFuzzy={formData.assignee_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, assignee_fuzzy: checked }));
                            onFormChange("assignee_fuzzy", checked);
                        }}
                    />
                </div>

                {/* 型式 */}
                <div className="form-row">
                    <SearchFieldRow
                        label="型式"
                        name="product_model"
                        value={formData.product_model}
                        onChange={handleChange}
                        condition={formData.product_model_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, product_model_condition: val }));
                            onFormChange("product_model_condition", val);
                        }}
                        isFuzzy={formData.product_model_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, product_model_fuzzy: checked }));
                            onFormChange("product_model_fuzzy", checked);
                        }}
                    />
                </div>

                {/* 図面タイプ */}
                <div className="form-row">
                    <div className="search-field">
                        <label>図面タイプ（計装・システム制御）</label>
                        <select
                            name="document_type"
                            value={formData.document_type || ""}
                            onChange={handleChange}
                            style={{ flex: '1', width: '100%', border: '1px solid #fff' }}
                        >
                            <option value=""></option>
                            {document_type?.map((item) => (
                                <option key={item.display_idx} value={item.document_type}>
                                    {item.document_type_text}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* 品種名 */}
                <div className="form-row">
                    <SearchFieldRow
                        label="品種名"
                        name="variety_name"
                        value={formData.variety_name}
                        onChange={handleChange}
                        condition={formData.variety_name_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, variety_name_condition: val }));
                            onFormChange("variety_name_condition", val);
                        }}
                        isFuzzy={formData.variety_name_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, variety_name_fuzzy: checked }));
                            onFormChange("variety_name_fuzzy", checked);
                        }}
                    />
                </div>

                {/* 備考 */}
                <div className="form-row">
                    <SearchFieldRow
                        label="備考"
                        name="remarks"
                        value={formData.remarks}
                        onChange={handleChange}
                        condition={formData.remarks_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, remarks_condition: val }));
                            onFormChange("remarks_condition", val);
                        }}
                        isFuzzy={formData.remarks_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, remarks_fuzzy: checked }));
                            onFormChange("remarks_fuzzy", checked);
                        }}
                    />
                </div>

                {/* タイトル */}
                <div className="form-row">
                    <SearchFieldRow
                        label="タイトル"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        condition={formData.title_condition}
                        onConditionChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ ...prev, title_condition: val }));
                            onFormChange("title_condition", val);
                        }}
                        isFuzzy={formData.title_fuzzy}
                        onFuzzyChange={(checked) => {
                            setFormData(prev => ({ ...prev, title_fuzzy: checked }));
                            onFormChange("title_fuzzy", checked);
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default KeisouForm;
